import { useState } from 'react'
import { Alert } from 'reactstrap'
import { useSelector } from 'react-redux'
import { VehicleCertQuery } from '../../services/endpoints/service29'
import useCertList from './useCertList'
import DataGrid, { Column, Paging, Sorting } from 'devextreme-react/data-grid'
import { selectIsAdmin } from '../../redux/slices'
import { certSortOrderFor, handleCertSortChange, makeStatusRenderer, renderDate, renderUser } from './Service29Utils'
import PageSizeSelect from './PageSizeSelect'
import CertPager from './CertPager'
import CertFilters, {
  CertDateRange,
  CertFilterField,
  CertFilterValues,
  CertStatusOption,
  dateRangeParams
} from './CertFilters'

const HISTORY_STATUSES = ['revoked', 'expired', 'failed', 'cancelled']

const FILTER_FIELDS: CertFilterField[] = [
  'vehicleVin',
  'deviceSerial',
  'requestedByUsername',
  'revokedByUsername'
]

const STATUS_OPTIONS: CertStatusOption[] = [
  { value: '', label: 'All Statuses' },
  { value: 'revoked', label: 'Revoked' },
  { value: 'expired', label: 'Expired' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' }
]

const DATE_RANGES: CertDateRange[] = [
  { field: 'deletedAt', label: 'Revoked/Expired At' }
]

const BASE_QUERY: VehicleCertQuery = {
  status: HISTORY_STATUSES,
  showAll: true,
  sort: 'deletedAt',
  order: 'desc',
  max: 50,
  offset: 0
}

const renderStatus = makeStatusRenderer({
  revoked: { label: 'Revoked', color: 'danger' },
  expired: { label: 'Expired', color: 'secondary' },
  failed: { label: 'Failed', color: 'danger' },
  cancelled: { label: 'Cancelled', color: 'warning' }
})

const Service29HistoryTab = () => {
  const isAdmin = useSelector(selectIsAdmin)
  const [query, setQuery] = useState<VehicleCertQuery>(BASE_QUERY)
  const { certs, total, error } = useCertList(isAdmin, query)

  const handleSearch = (filters: CertFilterValues) => {
    setQuery((prev) => ({
      ...prev,
      vehicleVin: filters.vehicleVin.trim() || undefined,
      deviceSerial: filters.deviceSerial.trim() || undefined,
      requestedByUsername: filters.requestedByUsername.trim() || undefined,
      revokedByUsername: filters.revokedByUsername.trim() || undefined,
      ...dateRangeParams(filters.dates),
      status: filters.status ? [filters.status] : HISTORY_STATUSES,
      offset: 0
    }))
  }

  if (!isAdmin) return null

  return (
    <section className="cert-tab-section">
      {error && <Alert color="danger">{error}</Alert>}
      <CertFilters
        fields={FILTER_FIELDS}
        statusOptions={STATUS_OPTIONS}
        dateRanges={DATE_RANGES}
        onSearch={handleSearch}
      />
      <menu className="cert-tab-toolbar">
        <li style={{ marginLeft: 'auto' }}>
          <PageSizeSelect
            value={query.max ?? 50}
            onChange={(max) =>
              setQuery((prev) => ({ ...prev, max, offset: 0 }))
            }
          />
        </li>
      </menu>
      <DataGrid
        dataSource={certs}
        keyExpr="id"
        showBorders={true}
        rowAlternationEnabled={true}
        columnAutoWidth={true}
        allowColumnResizing={true}
        onOptionChanged={(e) => handleCertSortChange(e, setQuery, BASE_QUERY)}
      >
        <Paging enabled={false} />
        <Sorting mode="single" />
        <Column
          dataField="vehicle.displayName"
          caption="Vehicle"
          allowSorting={false}
        />
        <Column dataField="vehicle.vin" caption="VIN" allowSorting={false} />
        <Column dataField="device.serial" caption="Device" allowSorting={false} />
        <Column
          dataField="status"
          caption="Status"
          cellRender={renderStatus}
          sortOrder={certSortOrderFor(query, 'status')}
        />
        <Column
          dataField="requestedBy"
          caption="Requested By"
          cellRender={renderUser}
          allowSorting={false}
        />
        <Column
          dataField="revokedBy"
          caption="Revoked By"
          cellRender={renderUser}
          allowSorting={false}
        />
        <Column
          dataField="deletedAt"
          caption="Revoked/Expired At"
          cellRender={renderDate}
          sortOrder={certSortOrderFor(query, 'deletedAt')}
        />
      </DataGrid>
      <CertPager
        total={total}
        offset={query.offset ?? 0}
        max={query.max ?? 50}
        onPageChange={(offset) => setQuery((prev) => ({ ...prev, offset }))}
      />
    </section>
  )
}

export default Service29HistoryTab
